// Changes in SharePoint that HR admins should know about: a source folder that's gone or moved,
// and approved documents that were deleted, moved, renamed or archived. The hourly sync records
// each change once; Admin shows the open ones under "Needs attention", and Ask HR admins get one
// email per sync that finds something new.

export type AlertKind = "source_missing" | "source_moved" | "doc_removed" | "doc_moved" | "doc_archived";

export interface Alert {
  // One per change. Seeing the same change again only updates lastSeenAt, so nobody is told twice.
  key: string;
  kind: AlertKind;
  action: boolean; // someone needs to do something; false: for information
  title: string;
  detail: string;
  url?: string; // where to look in SharePoint
  sourceId?: string;
  docId?: string;
  createdAt: string;
  lastSeenAt: string;
  resolvedAt?: string; // the sync saw it put right
  dismissedAt?: string;
  dismissedBy?: string;
  emailedAt?: string;
}

export type NewAlert = Omit<Alert, "createdAt" | "lastSeenAt" | "resolvedAt" | "dismissedAt" | "dismissedBy" | "emailedAt">;

export const isOpen = (a: Pick<Alert, "resolvedAt" | "dismissedAt">): boolean => !a.resolvedAt && !a.dismissedAt;

export const alertKey = {
  sourceMissing: (sourceId: string) => `source-missing:${sourceId}`,
  sourceMoved: (sourceId: string, path: string) => `source-moved:${sourceId}:${path}`,
  docRemoved: (docId: string) => `doc-removed:${docId}`,
  docMoved: (docId: string, path: string, name: string) => `doc-moved:${docId}:${path}/${name}`,
  docArchived: (docId: string) => `doc-archived:${docId}`,
};

// Sources are about where Ask HR reads (HR.Admin); the rest are about documents (HR.Manager too).
export const isSourceAlert = (a: Pick<Alert, "kind">): boolean => a.kind === "source_missing" || a.kind === "source_moved";
